// Audit log viewer (master only) — Sprint 5.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { SettingsCard, inputStyle } from './_shared';

export function AuditLogPage() {
  const [page, setPage] = useState(1);
  const [actorType, setActorType] = useState<'admin' | 'system' | 'patient' | ''>('');
  const [action, setAction] = useState('');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const actionsQuery = useQuery({
    queryKey: ['audit', 'actions'],
    queryFn: () => api.listAuditActions(),
  });

  const logQuery = useQuery({
    queryKey: ['audit', 'log', { page, actorType, action, search }],
    queryFn: () =>
      api.listAuditLog({
        page,
        page_size: 50,
        actor_type: actorType || undefined,
        action: action || undefined,
        search: search || undefined,
      }),
    staleTime: 5_000,
  });

  function downloadCsv() {
    const rows = logQuery.data?.rows ?? [];
    const csv = [
      'created_at,actor_type,actor_email,action,target_type,target_id,details',
      ...rows.map((r) => {
        const fields = [
          r.created_at,
          r.actor_type,
          r.actor_email ?? '',
          r.action,
          r.target_type ?? '',
          r.target_id ?? '',
          JSON.stringify(r.details).replace(/"/g, '""'),
        ];
        return fields.map((f) => `"${f}"`).join(',');
      }),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audiq-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <SettingsCard
      kicker="Audit log"
      title="Registro attività"
      description="Tutte le azioni admin, eventi di sicurezza e accessi a dati sensibili. Append-only — non modificabile."
      rightSlot={
        <button
          onClick={downloadCsv}
          disabled={!logQuery.data?.rows.length}
          className="rounded-md border px-3 py-1.5 text-xs disabled:opacity-40"
          style={{ borderColor: 'var(--line)', color: 'var(--ink)' }}
        >
          Scarica CSV pagina
        </button>
      }
    >
      <div className="mb-3 grid grid-cols-1 gap-2 md:grid-cols-3">
        <select
          className="rounded-md border px-3 py-2 text-sm"
          style={inputStyle()}
          value={actorType}
          onChange={(e) => {
            setPage(1);
            setActorType(e.target.value as typeof actorType);
          }}
        >
          <option value="">Tutti gli attori</option>
          <option value="admin">admin</option>
          <option value="system">system</option>
          <option value="patient">patient</option>
        </select>

        <select
          className="rounded-md border px-3 py-2 text-sm"
          style={inputStyle()}
          value={action}
          onChange={(e) => {
            setPage(1);
            setAction(e.target.value);
          }}
        >
          <option value="">Tutte le azioni</option>
          {actionsQuery.data?.actions.map((a) => (
            <option key={a.action} value={a.action}>
              {a.action} ({a.count})
            </option>
          ))}
        </select>

        <input
          className="rounded-md border px-3 py-2 text-sm"
          style={inputStyle()}
          placeholder="Cerca nei dettagli o azione…"
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
        />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ color: 'var(--mute)' }} className="mono text-xs">
              <th className="ps-1 pe-2 py-2 text-start">Quando</th>
              <th className="px-2 py-2 text-start">Attore</th>
              <th className="px-2 py-2 text-start">Azione</th>
              <th className="px-2 py-2 text-start">Target</th>
              <th className="px-2 py-2 text-start">Dettagli</th>
            </tr>
          </thead>
          <tbody>
            {logQuery.data?.rows.map((row) => {
              const isExpanded = expanded[row.id];
              const detailsKeys = Object.keys(row.details ?? {});
              return (
                <tr key={row.id} style={{ borderTop: '1px solid var(--line)' }}>
                  <td className="ps-1 pe-2 py-2 mono text-xs" style={{ color: 'var(--mute)' }}>
                    {new Date(row.created_at).toLocaleString('it-IT')}
                  </td>
                  <td className="px-2 py-2 text-xs">
                    <span style={{ color: 'var(--ink)' }}>{row.actor_email ?? row.actor_type}</span>
                    <br />
                    <span className="mono" style={{ color: 'var(--mute)' }}>
                      {row.actor_type}
                    </span>
                  </td>
                  <td className="px-2 py-2 mono text-xs" style={{ color: 'var(--ink)' }}>
                    {row.action}
                  </td>
                  <td className="px-2 py-2 mono text-xs" style={{ color: 'var(--mute)' }}>
                    {row.target_type ? `${row.target_type}/${row.target_id ?? '?'}` : '—'}
                  </td>
                  <td className="px-2 py-2 text-xs">
                    {detailsKeys.length === 0 ? (
                      <span style={{ color: 'var(--mute)' }}>—</span>
                    ) : isExpanded ? (
                      <pre
                        className="mono whitespace-pre-wrap break-all rounded-md p-2"
                        style={{ background: 'var(--soft)', color: 'var(--ink)' }}
                      >
                        {JSON.stringify(row.details, null, 2)}
                      </pre>
                    ) : (
                      <button
                        onClick={() => setExpanded((e) => ({ ...e, [row.id]: true }))}
                        className="underline"
                        style={{ color: 'var(--mute)' }}
                      >
                        {detailsKeys.slice(0, 3).join(', ')}
                        {detailsKeys.length > 3 && '…'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div
        className="mt-4 flex items-center justify-between text-sm"
        style={{ color: 'var(--mute)' }}
      >
        <span>
          {logQuery.data
            ? `Pagina ${logQuery.data.page} / ${logQuery.data.total_pages} · ${logQuery.data.total_count} righe totali`
            : 'Caricamento…'}
        </span>
        <div className="flex gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="rounded-md border px-3 py-1.5 text-xs disabled:opacity-40"
            style={{ borderColor: 'var(--line)', color: 'var(--ink)' }}
          >
            ← Precedente
          </button>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={!logQuery.data || page >= logQuery.data.total_pages}
            className="rounded-md border px-3 py-1.5 text-xs disabled:opacity-40"
            style={{ borderColor: 'var(--line)', color: 'var(--ink)' }}
          >
            Successiva →
          </button>
        </div>
      </div>
    </SettingsCard>
  );
}
